import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, User, Users, Check, Plus, X } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CapScanSetupPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [scanType, setScanType] = useState("");
  const [maps, setMaps] = useState([]);
  const [selectedMap, setSelectedMap] = useState("");
  const [employees, setEmployees] = useState([{ name: "", role: "", department: "", years_in_role: "" }]);
  const [method, setMethod] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (user) axios.get(`${API}/api/maps`, { withCredentials: true }).then((r) => setMaps(r.data)).catch(() => setMaps([]));
  }, [user, loading, navigate]);

  const addEmployee = () => { if (employees.length < 20) setEmployees([...employees, { name: "", role: "", department: "", years_in_role: "" }]); };
  const removeEmployee = (i) => setEmployees(employees.filter((_, idx) => idx !== i));
  const updateEmployee = (i, field, val) => {
    const copy = [...employees];
    copy[i][field] = val;
    setEmployees(copy);
  };

  const canProceedStep3 = () => {
    if (scanType === "individual") return employees[0]?.name && employees[0]?.role;
    return employees.every((e) => e.name && e.role);
  };

  const handleStart = async () => {
    setCreating(true);
    setError("");
    try {
      const res = await axios.post(`${API}/api/capscan/create`, {
        scan_type: scanType, map_id: selectedMap,
        employees: scanType === "individual" ? employees.slice(0, 1) : employees,
        rating_method: method,
      }, { withCredentials: true });
      navigate(`/capscan/rate/${res.data.scan_id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to create scan");
    } finally {
      setCreating(false);
    }
  };

  if (loading || !user) return <div className="min-h-screen bg-cream flex items-center justify-center pt-16"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-cream pt-28 pb-20 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/capscan" className="inline-flex items-center gap-2 text-navy/60 hover:text-gold font-dm text-sm mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to CapScan
        </Link>

        {/* Progress steps */}
        <div className="flex items-center justify-between mb-8" data-testid="capscan-step-indicator">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-dm font-bold text-sm ${step >= s ? "bg-gold text-navy" : "bg-white text-navy/40 border-2 border-navy/10"}`}>{step > s ? <Check className="w-5 h-5" /> : s}</div>
              {s < 4 && <div className={`h-1 flex-1 mx-2 rounded ${step > s ? "bg-gold" : "bg-navy/10"}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          {/* Step 1 */}
          {step === 1 && (
            <div data-testid="capscan-step-1">
              <h2 className="font-playfair text-navy text-2xl font-bold mb-2">Step 1 — Select scan type</h2>
              <p className="font-dm text-navy/60 text-sm mb-6">Choose whether you're rating one person or a team.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <button onClick={() => { setScanType("individual"); setEmployees([{ name: "", role: "", department: "", years_in_role: "" }]); }} data-testid="select-individual"
                  className={`text-left rounded-xl p-6 border-2 transition-all ${scanType === "individual" ? "border-gold bg-gold/5" : "border-navy/10 hover:border-gold/50"}`}>
                  <div className="text-3xl mb-2">👤</div>
                  <h3 className="font-playfair text-navy text-lg font-bold mb-1">Individual CapScan</h3>
                  <p className="font-dm text-navy/60 text-xs mb-2">Rate one employee across all skills in the selected competency map.</p>
                  <p className="font-dm text-gold text-xs font-bold">2 intelligence tokens</p>
                </button>
                <button onClick={() => setScanType("team")} data-testid="select-team"
                  className={`text-left rounded-xl p-6 border-2 transition-all ${scanType === "team" ? "border-gold bg-gold/5" : "border-navy/10 hover:border-gold/50"}`}>
                  <div className="text-3xl mb-2">👥</div>
                  <h3 className="font-playfair text-navy text-lg font-bold mb-1">Team CapScan</h3>
                  <p className="font-dm text-navy/60 text-xs mb-2">Rate multiple employees. Generate one report per person plus a team overview.</p>
                  <p className="font-dm text-gold text-xs font-bold">5 tokens (up to 10) · 8 tokens (11–20)</p>
                </button>
              </div>
              <button onClick={() => setStep(2)} disabled={!scanType} data-testid="step1-next" className="mt-6 w-full bg-navy text-gold font-dm font-bold py-3 rounded-lg hover:bg-navy/90 transition-colors disabled:opacity-50">Next</button>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div data-testid="capscan-step-2">
              <h2 className="font-playfair text-navy text-2xl font-bold mb-2">Step 2 — Select competency map</h2>
              <p className="font-dm text-navy/60 text-sm mb-6">Pick the map whose skills you want to rate against.</p>
              {maps.length === 0 ? (
                <div className="text-center py-8">
                  <p className="font-dm text-navy/60 text-sm mb-4">You haven't generated any competency maps yet.</p>
                  <Link to="/generate" className="inline-block bg-gold text-navy font-dm font-bold px-6 py-3 rounded-lg hover:bg-gold/90 transition-colors">Generate one now →</Link>
                </div>
              ) : (
                <select value={selectedMap} onChange={(e) => setSelectedMap(e.target.value)} data-testid="select-map" className="w-full border border-navy/20 rounded-lg px-4 py-3 font-dm text-sm focus:border-gold focus:outline-none">
                  <option value="">— Select a map —</option>
                  {maps.map((m) => (
                    <option key={m._id} value={m._id}>{m.job_role} · {m.company_name} · {new Date(m.created_at).toLocaleDateString("en-IN")}</option>
                  ))}
                </select>
              )}
              <div className="flex gap-3 mt-6">
                <button onClick={() => setStep(1)} className="font-dm text-navy/60 px-4 py-3 hover:text-navy transition-colors">← Back</button>
                <button onClick={() => setStep(3)} disabled={!selectedMap} data-testid="step2-next" className="flex-1 bg-navy text-gold font-dm font-bold py-3 rounded-lg hover:bg-navy/90 transition-colors disabled:opacity-50">Next</button>
              </div>
            </div>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <div data-testid="capscan-step-3">
              <h2 className="font-playfair text-navy text-2xl font-bold mb-2">Step 3 — Employee details</h2>
              <p className="font-dm text-navy/60 text-sm mb-6">{scanType === "individual" ? "Tell us about the person you're rating." : `Add details for each team member (max 20). Currently ${employees.length}.`}</p>
              <div className="space-y-3">
                {employees.map((emp, i) => (
                  <div key={i} className="border border-navy/10 rounded-lg p-4" data-testid={`employee-row-${i}`}>
                    {scanType === "team" && (
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-dm text-navy text-xs font-bold uppercase">Person {i + 1}</p>
                        {employees.length > 1 && <button onClick={() => removeEmployee(i)} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>}
                      </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-3">
                      <input value={emp.name} onChange={(e) => updateEmployee(i, "name", e.target.value)} placeholder="Full name *" data-testid={`employee-name-${i}`} className="border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none" />
                      <input value={emp.role} onChange={(e) => updateEmployee(i, "role", e.target.value)} placeholder="Job title / Role *" data-testid={`employee-role-${i}`} className="border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none" />
                      <input value={emp.department} onChange={(e) => updateEmployee(i, "department", e.target.value)} placeholder="Department" className="border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none" />
                      <select value={emp.years_in_role} onChange={(e) => updateEmployee(i, "years_in_role", e.target.value)} className="border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none">
                        <option value="">Years in role…</option>
                        <option>&lt;1</option><option>1-2</option><option>3-5</option><option>5+</option>
                      </select>
                    </div>
                  </div>
                ))}
                {scanType === "team" && employees.length < 20 && (
                  <button onClick={addEmployee} data-testid="add-employee" className="w-full border-2 border-dashed border-gold text-gold font-dm font-semibold py-3 rounded-lg hover:bg-gold/5 transition-colors flex items-center justify-center gap-2">
                    <Plus className="w-4 h-4" /> Add another person
                  </button>
                )}
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setStep(2)} className="font-dm text-navy/60 px-4 py-3 hover:text-navy transition-colors">← Back</button>
                <button onClick={() => setStep(4)} disabled={!canProceedStep3()} data-testid="step3-next" className="flex-1 bg-navy text-gold font-dm font-bold py-3 rounded-lg hover:bg-navy/90 transition-colors disabled:opacity-50">Next</button>
              </div>
            </div>
          )}

          {/* Step 4 */}
          {step === 4 && (
            <div data-testid="capscan-step-4">
              <h2 className="font-playfair text-navy text-2xl font-bold mb-2">Step 4 — How will you rate skills?</h2>
              <p className="font-dm text-navy/60 text-sm mb-6">Choose one method for this scan.</p>
              <div className="space-y-3 mb-4">
                {[
                  { key: "self", emoji: "🧑", title: "Self Assessment", desc: "The employee rates their own skills.", best: "Best for: Development conversations, individual planning.", active: true },
                  { key: "manager", emoji: "👔", title: "Manager Assessment", desc: "You (the manager) rate the employee's skills.", best: "Best for: Performance reviews, promotion decisions.", active: true },
                  { key: "360", emoji: "🔄", title: "360-Degree + AI Interview", desc: "Anonymous multi-rater + AI interview questions.", best: "Available in next platform update.", active: false },
                ].map((m) => (
                  <button key={m.key} onClick={() => m.active && setMethod(m.key)} disabled={!m.active} data-testid={`method-${m.key}`}
                    className={`w-full text-left rounded-xl p-5 border-2 transition-all relative ${method === m.key ? "border-gold bg-gold/5" : m.active ? "border-navy/10 hover:border-gold/50" : "border-navy/5 opacity-50 cursor-not-allowed"}`}>
                    {!m.active && <span className="absolute top-3 right-3 bg-gold/20 text-gold text-[10px] font-bold px-2 py-1 rounded-full">Coming Soon — Strike 2</span>}
                    <div className="flex items-start gap-4">
                      <div className="text-3xl">{m.emoji}</div>
                      <div className="flex-1">
                        <h3 className="font-playfair text-navy text-lg font-bold mb-1">{m.title}</h3>
                        <p className="font-dm text-navy/70 text-sm">{m.desc}</p>
                        <p className="font-dm text-navy/50 text-xs italic mt-1">{m.best}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <p className="font-dm text-navy/50 text-xs italic mb-4">360-degree rater flow and AI Interview Questions are being added in the next platform update.</p>
              {error && <p className="font-dm text-red-600 text-sm mb-3">{error}</p>}
              <div className="flex gap-3">
                <button onClick={() => setStep(3)} className="font-dm text-navy/60 px-4 py-3 hover:text-navy transition-colors">← Back</button>
                <button onClick={handleStart} disabled={!method || creating} data-testid="start-rating-btn" className="flex-1 bg-navy text-gold font-dm font-bold py-3 rounded-lg hover:bg-navy/90 transition-colors disabled:opacity-50">
                  {creating ? "Creating scan..." : "Start Rating →"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
